<template>
    <div class="w-full relative select-none z-0">
        <!-- Slider Track wrapper -->
        <div class="h-2.5 bg-surface-contained rounded-full pr-3 flex flex-row items-center" @click.self="handleTrackClick">
            <!-- Small element to hack some padding around the track -->
            <!-- Colored if a single slider -->
            <div class="w-3 h-full rounded-l-full" :class="rangeStartBaseClasses"></div>
            <!-- Slider Track -->
            <div class="h-full relative grow" ref="slider" @click.self="handleTrackClick">
                <!-- Selected Range -->
                <div
                    :class="rangeBaseClasses"
                    :style="{
                        left: minPosition + '%',
                        width: maxPosition - minPosition + '%'
                    }"
                ></div>

                <!-- Max handle -->
                <div
                    :style="{ left: maxPosition + '%' }"
                    :class="[handleBaseClasses, { 'shadow-md': dragging === 'max' }, 'z-30']"
                    @mousedown="startDrag('max', $event)"
                    @touchstart.prevent="startDrag('max', $event)"
                ></div>

                <!-- Max tooltip -->
                <Tooltip
                    :label="maxTooltipLabel"
                    position="up"
                    :offset="8"
                    :class="[virtualHandleBaseClasses]"
                    :hide-tooltip="isTooltipMerged"
                    :force-show="!!dragging || moving"
                    ref="maxTooltip"
                    :allowAxisFlip="false"
                    :style="{
                        left: maxPosition + '%'
                    }"
                ></Tooltip>

                <template v-if="isDoubleSlider">
                    <!-- Min handle -->
                    <div
                        :style="{ left: minPosition + '%' }"
                        :class="[handleBaseClasses, { 'shadow-md': dragging === 'min' }]"
                        @mousedown="startDrag('min', $event)"
                        @touchstart.prevent="startDrag('min', $event)"
                    ></div>

                    <!-- Min tooltip -->
                    <Tooltip
                        :label="minTooltipLabel"
                        position="up"
                        :offset="8"
                        :class="[virtualHandleBaseClasses]"
                        :hide-tooltip="isTooltipMerged"
                        :force-show="!!dragging || moving"
                        ref="minTooltip"
                        :allowAxisFlip="false"
                        :style="{
                            left: minPosition + '%'
                        }"
                    ></Tooltip>

                    <!-- Merged Tooltip -->
                    <Tooltip
                        :hide-tooltip="!isTooltipMerged"
                        :label="mergedTooltipLabel"
                        position="up"
                        :offset="8"
                        :class="[virtualHandleBaseClasses]"
                        :force-show="!!dragging || moving"
                        :style="{ left: minPosition + (maxPosition - minPosition) / 2 + '%' }"
                        :allowAxisFlip="false"
                        width="lg"
                    />
                </template>
            </div>
        </div>
        <div v-if="props.options.showTickLabels" class="text-sub text-sm font-medium mt-4 flex flex-row justify-stretch w-full">
            <div class="grow basis-0">{{ props.minValue }}</div>
            <div class="grow basis-0 text-center">{{ Math.round((props.maxValue - props.minValue) / 2) }}</div>
            <div class="grow basis-0 text-right">{{ props.maxValue }}</div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, onUnmounted, useTemplateRef, watch, nextTick } from 'vue'
    import { type TimeZone } from '../../types/time.js'
    import { DateTime } from 'luxon'
    import Tooltip from '../elements/Tooltip.vue'
    import { formatNumber } from '../../utils/number.js'
    type UnitType = 'number' | 'timestamp'
    type TimestampUnit = 'seconds' | 'milliseconds'

    type Options = {
        prefix?: string
        suffix?: string
        unitType: UnitType
        locale: string
        timeZone: TimeZone
        step: number
        showTickLabels: boolean
        timestampUnit: TimestampUnit
        rangeCharacter: string
    }

    type PartialOptions = Partial<Options>

    const OPTIONS_DEFAULT: Options = {
        unitType: 'number',
        locale: 'en-US',
        timeZone: 'system',
        step: 1,
        showTickLabels: false,
        timestampUnit: 'seconds',
        rangeCharacter: 'to'
    } as const

    const rangeValue = defineModel<number | [number, number]>({ default: () => 0, required: true })

    const props = withDefaults(
        defineProps<{
            class?: string
            options?: PartialOptions
            maxValue?: number
            minValue?: number
            variant?: 'blue' | 'neutral' | 'primary'
        }>(),
        {
            maxValue: 100,
            minValue: 0,
            variant: 'primary',
            options: () => ({})
        }
    )

    // Template refs
    const minTooltipRef = useTemplateRef('minTooltip')
    const maxTooltipRef = useTemplateRef('maxTooltip')

    // Reactive state
    const min = ref<number>(props.minValue)
    const max = ref<number>(props.maxValue)
    const dragging = ref<'min' | 'max' | null>(null)
    const moving = ref<boolean>(false)
    const slider = ref<HTMLElement | null>(null)
    const transitionOnClick = ref<boolean>(false)
    const options = computed<Options>(() => ({
        ...OPTIONS_DEFAULT,
        ...props.options
    }))

    const step = computed(() => options.value.step ?? OPTIONS_DEFAULT.step)
    const decimalPlaces = computed(() => {
        const decimalIndex = step.value.toString().indexOf('.')
        return decimalIndex >= 0 ? step.value.toString().length - decimalIndex - 1 : 0
    })

    if (typeof rangeValue.value === 'number') max.value = rangeValue.value
    if (typeof rangeValue.value === 'object') {
        min.value = rangeValue.value[0]
        max.value = rangeValue.value[1]
    }

    // Computed properties
    const isDoubleSlider = computed(() => {
        if (typeof rangeValue.value === 'object' && rangeValue.value.length === 2) return true
        else return false
    })

    const minPosition = computed(() => {
        if (!isDoubleSlider.value) return 0
        return ((min.value - props.minValue) / (props.maxValue - props.minValue)) * 100
    })

    const maxPosition = computed(() => {
        return ((max.value - props.minValue) / (props.maxValue - props.minValue)) * 100
    })
    const tooltipPrefix = computed(() => {
        return options.value.prefix ?? ''
    })
    const tooltipSuffix = computed(() => {
        return options.value.suffix ?? ''
    })

    const isTooltipMerged = ref(false)

    const checkTooltipOverlap = () => {
        // Ensure both refs are available
        if (!minTooltipRef.value || !maxTooltipRef.value) return

        // Get bounding rectangles of both tooltip refs in the tooltip component
        const minTooltip = minTooltipRef.value.$refs?.tooltip as HTMLElement
        const maxTooltip = maxTooltipRef.value.$refs?.tooltip as HTMLElement

        // Ensure both elements are available
        if (!minTooltip || !maxTooltip) return

        const minRect = minTooltip.getBoundingClientRect()
        const maxRect = maxTooltip.getBoundingClientRect()

        // Check if the two tooltips overlap
        const isOverlapping = minRect.left < maxRect.right && minRect.right > maxRect.left && minRect.top < maxRect.bottom && minRect.bottom > maxRect.top
        isTooltipMerged.value = isOverlapping
    }

    // Event handlers
    const startDrag = (handle: 'min' | 'max', event: MouseEvent | TouchEvent) => {
        dragging.value = handle
        window.addEventListener('mousemove', onDrag)
        window.addEventListener('mouseup', stopDrag)
        window.addEventListener('touchmove', onDrag, { passive: false })
        window.addEventListener('touchend', stopDrag)
    }

    const onDrag = (event: MouseEvent | TouchEvent) => {
        checkTooltipOverlap()

        event.preventDefault()
        let clientX: number

        if (event instanceof MouseEvent) {
            clientX = event.clientX
        } else if (event instanceof TouchEvent) {
            clientX = event.touches[0].clientX
        } else {
            return
        }

        if (!slider.value) return
        const rect = slider.value.getBoundingClientRect()
        let percentage = ((clientX - rect.left) / rect.width) * 100
        percentage = Math.max(0, Math.min(100, percentage))
        let value = props.minValue + (percentage / 100) * (props.maxValue - props.minValue)
        value = Math.round(value / step.value) * step.value

        if (dragging.value === 'min') {
            const maxValue = max.value - step.value
            if (value > maxValue) value = maxValue
            if (value < props.minValue) value = props.minValue
            min.value = Number(value.toFixed(decimalPlaces.value))
        } else if (dragging.value === 'max') {
            const minValue = isDoubleSlider.value ? min.value + step.value : min.value
            if (value < minValue) value = minValue
            if (value > props.maxValue) value = props.maxValue
            max.value = Number(value.toFixed(decimalPlaces.value))
        }
    }

    const stopDrag = () => {
        dragging.value = null
        window.removeEventListener('mousemove', onDrag)
        window.removeEventListener('mouseup', stopDrag)
        window.removeEventListener('touchmove', onDrag)
        window.removeEventListener('touchend', stopDrag)
    }

    onUnmounted(() => {
        stopDrag()
    })

    const handleTrackClick = (event: MouseEvent | TouchEvent) => {
        event.preventDefault()
        let clientX: number

        if (event instanceof MouseEvent) {
            clientX = event.clientX
        } else if (event instanceof TouchEvent) {
            clientX = event.touches[0].clientX
        } else {
            return
        }

        if (!slider.value) return
        const rect = slider.value.getBoundingClientRect()
        let percentage = ((clientX - rect.left) / rect.width) * 100
        percentage = Math.max(0, Math.min(100, percentage))
        let value = props.minValue + (percentage / 100) * (props.maxValue - props.minValue)
        value = Math.round(value / step.value) * step.value

        const distanceToMin = Math.abs(min.value - value)
        const distanceToMax = Math.abs(max.value - value)
        if (distanceToMin < distanceToMax && isDoubleSlider.value) {
            if (value <= max.value) min.value = value
        } else {
            if (value >= min.value) max.value = value
        }

        // Temporarily transition track and handle positions
        transitionOnClick.value = true
        setTimeout(() => (transitionOnClick.value = false), 100)

        // Temporarily show tooltips
        moving.value = true
        nextTick(() => checkTooltipOverlap())
        setTimeout(() => (moving.value = false), 400)
    }

    watch(min, (newValue) => {
        if (typeof rangeValue.value === 'object' && rangeValue.value.length === 2) rangeValue.value[0] = newValue
    })

    watch(max, (newValue) => {
        if (typeof rangeValue.value === 'object' && rangeValue.value.length === 2) rangeValue.value[1] = newValue
        else rangeValue.value = newValue
    })

    const handleBaseClasses = computed(() => {
        const variantOptions = {
            neutral: 'border-neutral-600',
            primary: 'border-green-500',
            blue: 'border-blue-500'
        }

        const transitionClasses = transitionOnClick.value ? 'transition-all duration-100' : 'transition-shadow duration-200'

        const baseClasses = 'w-8 h-8 bg-system-light border-[10px] rounded-full absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2 cursor-pointer shadow z-20'

        return [baseClasses, transitionClasses, variantOptions[props.variant]]
    })

    const virtualHandleBaseClasses = computed(() => {
        const transitionClasses = transitionOnClick.value ? 'transition-all duration-100' : 'transition-shadow duration-200'
        const baseClasses = 'w-8 h-8 bg-none border-[10px_solid_transparent] rounded-full absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2 pointer-events-none'
        return [baseClasses, transitionClasses]
    })

    const rangeBaseClasses = computed(() => {
        const variantOptions = {
            neutral: 'bg-gray-300',
            primary: 'bg-green-200',
            blue: 'bg-blue-200'
        }
        const transitionClasses = transitionOnClick.value ? 'transition-all duration-100' : ''
        const baseClasses = 'h-2.5 absolute z-10 pointer-events-none'
        return [baseClasses, transitionClasses, variantOptions[props.variant]]
    })

    const rangeStartBaseClasses = computed(() => {
        const variantOptions = {
            neutral: 'bg-gray-300',
            primary: 'bg-green-200',
            blue: 'bg-blue-200'
        }
        return isDoubleSlider.value ? '' : variantOptions[props.variant]
    })

    const minTooltipLabel = computed(() => {
        if (props.options.unitType === 'timestamp') return timestampToDate(min.value)

        const minimum = formatNumber(min.value, { decimals: decimalPlaces.value, minimumDecimals: decimalPlaces.value })
        return `${tooltipPrefix.value}${minimum}${tooltipSuffix.value}`
    })
    const maxTooltipLabel = computed(() => {
        if (props.options.unitType === 'timestamp') return timestampToDate(max.value)

        const maximum = formatNumber(max.value, { decimals: decimalPlaces.value, minimumDecimals: decimalPlaces.value })
        return `${tooltipPrefix.value}${maximum}${tooltipSuffix.value}`
    })
    const mergedTooltipLabel = computed(() => {
        if (props.options.unitType === 'timestamp') return `${timestampToDate(min.value)} – ${timestampToDate(max.value)}`

        const minimum = formatNumber(min.value, { decimals: decimalPlaces.value, minimumDecimals: decimalPlaces.value })
        const maximum = formatNumber(max.value, { decimals: decimalPlaces.value, minimumDecimals: decimalPlaces.value })
        return `${tooltipPrefix.value}${minimum} ${options.value.rangeCharacter} ${maximum}${tooltipSuffix.value}`
    })

    const timestampToDate = (timestamp: number) => {
        const timeZone = options.value?.timeZone
        const locale = options.value?.locale
        // const dateTime = options.value?.timestampUnit === 'seconds' ? DateTime.fromSeconds(timestamp) : DateTime.fromMillis(timestamp)
        const value = DateTime.fromSeconds(timestamp).setZone(timeZone).setLocale(locale)

        const formattedDate = value.toLocaleString({
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        })

        return formattedDate
    }
</script>
